import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Loader2, Image as ImageIcon, X } from 'lucide-react'
import { apiGet } from '../lib/api'

interface LibraryImage {
  id: string
  url: string
  filename: string
  altText: string
  tags: string
  sizeBytes: number
  mime: string
  uploadedBy: string
  createdAt: string
}

interface LibraryData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: LibraryImage[]
}

interface Props {
  open: boolean
  onClose: () => void
  onSelect: (url: string, image: LibraryImage) => void
  title?: string
}

/**
 * Modal selector del banco de imágenes.
 *
 * Uso:
 *   const [open, setOpen] = useState(false)
 *   <ImageLibraryPicker
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onSelect={(url) => {
 *       form.setValue('image', url)
 *       setOpen(false)
 *     }}
 *   />
 */
export function ImageLibraryPicker({ open, onClose, onSelect, title = 'Elegir del banco' }: Props) {
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [q])

  // Reset state when modal closes/reopens
  useEffect(() => {
    if (!open) {
      setQ('')
      setPage(1)
    }
  }, [open])

  const { data, isLoading, isFetching } = useQuery<LibraryData>({
    queryKey: ['admin-image-library-picker', q, page],
    queryFn: () => {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      params.set('page', String(page))
      return apiGet(`/api/admin/image-library?${params.toString()}`)
    },
    enabled: open,
  })

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/60 p-4">
      <div className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <h2 className="text-sm font-semibold text-ink-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="border-b border-ink-100 p-4">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, alt o tag…"
              autoFocus
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
        </div>

        <div className="relative flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
            </div>
          )}

          {data && data.data.length === 0 && (
            <div className="grid place-items-center py-16 text-center">
              <ImageIcon className="size-10 text-ink-300" />
              <p className="mt-3 text-sm font-medium text-ink-700">
                {q ? 'No hay resultados' : 'No hay imágenes en el banco'}
              </p>
              <p className="text-xs text-ink-500">
                {q ? 'Prueba con otra búsqueda.' : 'Sube imágenes desde Banco de imágenes.'}
              </p>
            </div>
          )}

          {data && data.data.length > 0 && (
            <>
              {isFetching && (
                <div className="absolute right-6 top-6 z-10 rounded-full bg-white p-1.5 shadow-md">
                  <Loader2 className="size-4 animate-spin text-brand-600" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {data.data.map((img) => (
                  <button
                    type="button"
                    key={img.id}
                    onClick={() => onSelect(img.url, img)}
                    title={img.filename}
                    className="group relative aspect-square overflow-hidden rounded-lg border border-ink-200 bg-ink-50 transition-all hover:border-brand-500 hover:shadow-md focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                  >
                    <img
                      src={img.url}
                      alt={img.altText || img.filename}
                      className="size-full object-contain"
                      loading="lazy"
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).src = '/img/sin-imagen.jpg'
                      }}
                    />
                    <div className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-ink-900/80 to-transparent px-2 py-1.5 text-left text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                      {img.filename}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {data && data.pages > 1 && (
          <div className="flex items-center justify-center gap-2 border-t border-ink-100 px-4 py-3">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="text-xs text-ink-600">
              Página {page} de {data.pages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={page >= data.pages}
              className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ImageLibraryPicker
